import React, { createContext, ReactNode, useState, useCallback, useEffect, useContext, useMemo } from 'react';

import { getUserState } from '../../../components/utils';
import { AllPermissionsType, PermissionsRolesType, Roles } from './permissionsTypes';

export interface User {
    fullName: string;
    role: Roles;
    permissions: PermissionsRolesType;
}

export type PermissionType = { permission: AllPermissionsType; role: Roles };

interface UserContextType {
    user?: User;
    hasPermission: (permission: PermissionType) => boolean;
    refreshUserData: () => Promise<void>;
}

const UserContext = createContext<UserContextType>({ hasPermission: () => false, refreshUserData: async () => {} });

interface UserProviderProps {
    children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | undefined>();

    const refreshUserData = useCallback(async () => {
        try {
            const data = (await getUserState()) as { user: User } | undefined;
            setUser(data?.user);
        } catch (error) {
            console.error('Failed to fetch user data:', error);
        }
    }, []);

    useEffect(() => {
        refreshUserData();
    }, [refreshUserData]);

    const hasPermission = useCallback(
        ({ permission, role }: PermissionType): boolean => {
            if (!user || !user.permissions) return false;

            const rolePermissions = user.permissions[permission];
            if (!rolePermissions) return false;

            return rolePermissions >= role;
        },
        [user]
    );

    const value = useMemo(
        () => ({
            user,
            hasPermission,
            refreshUserData
        }),
        [user, hasPermission, refreshUserData]
    );

    return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUserContext = (): UserContextType => useContext(UserContext);
